
import React, { useState, useEffect } from "react";
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Typography,
  IconButton,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { getAllPostSeasonActivitiesAction, createPostMonthlyTargetAction, clearCreatePostMonthlyTargetAction } from "../../../store/actions";
import PropTypes from "prop-types";

const validationSchema = Yup.object({
  post_target_id: Yup.string().required("Post Activity is required"),
  month_year: Yup.string().matches(/^\d{4}-(0[1-9]|1[0-2])$/, "Month must be in YYYY-MM format").required("Month is required"),
  dv_count: Yup.number().typeError("Dealer Visit must be a number").integer("Dealer Visit must be an integer").min(0, "Must be positive").required("Dealer Visit is required"),
  fv_count: Yup.number().typeError("Field Visit must be a number").integer("Field Visit must be an integer").min(0, "Must be positive").required("Field Visit is required"),
  fd_count: Yup.number().typeError("Field Day must be a number").integer("Field Day must be an integer").min(0, "Must be positive").required("Field Day is required"),
});

const PostMonthlyTarget = ({ open, onClose, onSubmit, defaultPostTargetId }) => {
  const dispatch = useDispatch();
  const [entries, setEntries] = useState([]);
  const { successData: postSeasonActivitiesData } = useSelector((state) => state.getAllPostSeasonActivitiesReducer || {});

  // Normalization helper for dropdown (flat array of post-season targets)
  const normalizePostSeasonTargets = (rawData) => {
    if (!Array.isArray(rawData) || rawData.length === 0) return [];
    const flatRows = [];
    const seenIds = new Set();

    // Always add top-level targets
    rawData.forEach((entry) => {
      (entry.targets || []).forEach((target) => {
        // Add the top-level target itself
        if (target.id && !seenIds.has(target.id)) {
          flatRows.push(target);
          seenIds.add(target.id);
        }
        // Add nested mandal post_targets
        if (target.mandals && Array.isArray(target.mandals)) {
          target.mandals.forEach((mandal) => {
            (mandal.post_targets || []).forEach((postTarget) => {
              if (postTarget.id && !seenIds.has(postTarget.id)) {
                flatRows.push(postTarget);
                seenIds.add(postTarget.id);
              }
            });
          });
        }
      });
    });
    return flatRows;
  };

  const postSeasonActivitiesList = normalizePostSeasonTargets(postSeasonActivitiesData?.data);
  const { successData: createSuccessData, loading: createLoading, error: createError } = useSelector(
    (state) => state.createPostMonthlyTargetReducer || {}
  );

  const parseCountValue = (value) => {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 0) return null;
    return parsed;
  };

  const formik = useFormik({
    initialValues: {
      post_target_id: defaultPostTargetId ? Number(defaultPostTargetId) : "",
      month_year: "",
      dv_count: "",
      fv_count: "",
      fd_count: "",
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      if (!values.post_target_id) {
        toast.error("Please select a Post-Season Activity first");
        return;
      }

      if (entries.some((entry) => entry.month_year === values.month_year)) {
        toast.error("This month is already added. Please choose a different month.");
        return;
      }
      
      // Get remaining counts for validation
      const selectedTarget = postSeasonActivitiesList.find(p => p.id === Number(values.post_target_id));
      if (!selectedTarget) {
        toast.error("Invalid post target selected");
        return;
      }
      
      const totalDv = selectedTarget.dv_count || 0;
      const totalFv = selectedTarget.fv_count || 0;
      const totalFd = selectedTarget.fd_count || 0;
      
      const usedDv = entries.reduce((sum, e) => sum + (e.dv_count || 0), 0);
      const usedFv = entries.reduce((sum, e) => sum + (e.fv_count || 0), 0);
      const usedFd = entries.reduce((sum, e) => sum + (e.fd_count || 0), 0);
      
      const remainingDv = totalDv - usedDv;
      const remainingFv = totalFv - usedFv;
      const remainingFd = totalFd - usedFd;
      
      // Validate against remaining counts
      const dvValue = parseCountValue(values.dv_count);
      const fvValue = parseCountValue(values.fv_count);
      const fdValue = parseCountValue(values.fd_count);

      if (dvValue === null || fvValue === null || fdValue === null) {
        toast.error("Dealer Visit, Field Visit, and Field Day must be non-negative integers");
        return;
      }
      
      if (dvValue > remainingDv) {
        toast.error(`Dealer Visit exceeds remaining count. Remaining: ${remainingDv} (Total: ${totalDv}, Used: ${usedDv})`);
        return;
      }
      if (fvValue > remainingFv) {
        toast.error(`Field Visit exceeds remaining count. Remaining: ${remainingFv} (Total: ${totalFv}, Used: ${usedFv})`);
        return;
      }
      if (fdValue > remainingFd) {
        toast.error(`Field Day exceeds remaining count. Remaining: ${remainingFd} (Total: ${totalFd}, Used: ${usedFd})`);
        return;
      }
      
      const newEntry = {
        post_target_id: Number(values.post_target_id),
        month_year: values.month_year,
        dv_count: dvValue,
        fv_count: fvValue,
        fd_count: fdValue,
      };
      setEntries((prev) => [...prev, newEntry]);
      formik.setFieldValue("month_year", "");
      formik.setFieldValue("dv_count", "");
      formik.setFieldValue("fv_count", "");
      formik.setFieldValue("fd_count", "");
      toast.success("Entry added successfully!");
    },
  });

  // Get the selected post target details to access counts (after formik is initialized)
  const selectedPostTarget = React.useMemo(() => {
    return postSeasonActivitiesList.find(p => p.id === Number(formik.values.post_target_id));
  }, [postSeasonActivitiesList, formik.values.post_target_id]);
  
  // Calculate remaining allowance for each field
  const remainingCounts = React.useMemo(() => {
    if (!selectedPostTarget) {
      return { dv: 0, fv: 0, fd: 0, totalDv: 0, totalFv: 0, totalFd: 0, usedDv: 0, usedFv: 0, usedFd: 0 };
    }
    
    const totalDv = selectedPostTarget.dv_count || 0;
    const totalFv = selectedPostTarget.fv_count || 0;
    const totalFd = selectedPostTarget.fd_count || 0;
    
    // Sum up already added entries
    const usedDv = entries.reduce((sum, e) => sum + (e.dv_count || 0), 0);
    const usedFv = entries.reduce((sum, e) => sum + (e.fv_count || 0), 0);
    const usedFd = entries.reduce((sum, e) => sum + (e.fd_count || 0), 0);
    
    return {
      dv: totalDv - usedDv,
      fv: totalFv - usedFv,
      fd: totalFd - usedFd,
      totalDv,
      totalFv,
      totalFd,
      usedDv,
      usedFv,
      usedFd
    };
  }, [selectedPostTarget, entries]);

  useEffect(() => {
    if (open) {
      dispatch(getAllPostSeasonActivitiesAction());
    }
    if (!open) {
      formik.resetForm();
      setEntries([]);
    }
    // eslint-disable-next-line
  }, [open, dispatch]);

  // Clear entries when post_target_id changes
  useEffect(() => {
    if (formik.values.post_target_id && entries.length > 0) {
      // Check if any entry has different post_target_id
      const hasDifferentTarget = entries.some(e => e.post_target_id !== Number(formik.values.post_target_id));
      if (hasDifferentTarget) {
        setEntries([]);
        toast.info("Entries cleared due to post target change");
      }
    }
    // eslint-disable-next-line
  }, [formik.values.post_target_id]);

  useEffect(() => {
    if (createSuccessData) {
      toast.success("Monthly target updated successfully!");
      dispatch(clearCreatePostMonthlyTargetAction());
      setEntries([]);
      formik.resetForm();
      onSubmit?.(createSuccessData);
      onClose?.();
    }
  }, [createSuccessData]);

  useEffect(() => {
    if (createError) {
      toast.error(createError?.message || "Failed to create monthly target");
      dispatch(clearCreatePostMonthlyTargetAction());
    }
  }, [createError]);

  const addEntry = () => {
    formik.handleSubmit();
  };

  const handleSubmitAll = async () => {
    if (!formik.values.post_target_id) {
      toast.error("Please select a Post-Season Activity");
      return;
    }
    if (entries.length === 0) {
      toast.error("Please add at least one monthly target entry");
      return;
    }

    const monthSet = new Set();
    for (const item of entries) {
      if (!item.month_year || monthSet.has(item.month_year)) {
        toast.error("Duplicate or invalid month found in entries. Please keep months unique.");
        return;
      }
      monthSet.add(item.month_year);
    }

    const selectedTarget = postSeasonActivitiesList.find((p) => p.id === Number(formik.values.post_target_id));
    if (!selectedTarget) {
      toast.error("Selected Post-Season Activity is invalid");
      return;
    }

    const totalDv = selectedTarget.dv_count || 0;
    const totalFv = selectedTarget.fv_count || 0;
    const totalFd = selectedTarget.fd_count || 0;

    const usedDv = entries.reduce((sum, e) => sum + (e.dv_count || 0), 0);
    const usedFv = entries.reduce((sum, e) => sum + (e.fv_count || 0), 0);
    const usedFd = entries.reduce((sum, e) => sum + (e.fd_count || 0), 0);

    if (usedDv > totalDv || usedFv > totalFv || usedFd > totalFd) {
      toast.error(
        `Total monthly allocation exceeds post target limit. DV: ${usedDv}/${totalDv}, FV: ${usedFv}/${totalFv}, FD: ${usedFd}/${totalFd}`
      );
      return;
    }

    const payload = { items: entries };
    await dispatch(createPostMonthlyTargetAction(payload));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 800, fontSize: 28 }}>
        Monthly Post Activity Update
        <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={7} mt={1}>
              {defaultPostTargetId ? (
                (() => {
                  const matched = postSeasonActivitiesList.find(p => p.id === Number(defaultPostTargetId));
                  const displayText = matched
                    ? `ID: ${matched.id} - ${matched.season?.name ?? ''} (${matched.season?.year ?? ''}) - ${matched.target_area_type ?? ''}`
                    : `Post Target ID: ${defaultPostTargetId}`;
                  return (
                    <TextField
                      fullWidth
                      label="Post-Season Activity"
                      value={displayText}
                      disabled
                      InputLabelProps={{ shrink: true }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 }  }}
                    />
                  );
                })()
              ) : (
                <FormControl
                  fullWidth
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                  error={formik.touched.post_target_id && Boolean(formik.errors.post_target_id)}
                >
                  <InputLabel id="postactivity-select-label">Post-Season Activity</InputLabel>
                  <Select
                    labelId="postactivity-select-label"
                    name="post_target_id"
                    value={formik.values.post_target_id}
                    label="Post-Season Activity"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    {postSeasonActivitiesList.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        ID: {p.id} - {p.season?.name} ({p.season?.year}) - {p.target_area_type}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.post_target_id && formik.errors.post_target_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {formik.errors.post_target_id}
                    </Typography>
                  )}
                </FormControl>
              )}
            </Grid>

            <Grid item xs={12} sm={3} mt={1}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  views={['year', 'month']}
                  label="Month (YYYY-MM)"
                  minDate={dayjs('2020-01-01')}
                  maxDate={dayjs('2099-12-31')}
                  value={formik.values.month_year ? dayjs(formik.values.month_year) : null}
                  onChange={value => {
                    formik.setFieldValue('month_year', value ? value.format('YYYY-MM') : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={formik.touched.month_year && Boolean(formik.errors.month_year)}
                      sx={{
                        mt: 0.5,
                        "& .MuiOutlinedInput-root": { borderRadius: 3 },
                        "& input": { borderRadius: 3 },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
              {formik.touched.month_year && formik.errors.month_year && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {formik.errors.month_year}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                name="dv_count"
                type="number"
                label="Dealer Visit"
                placeholder="Dealer Visit"
                value={formik.values.dv_count}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.dv_count && Boolean(formik.errors.dv_count)}
                helperText={
                  formik.touched.dv_count && formik.errors.dv_count 
                    ? formik.errors.dv_count 
                    : selectedPostTarget 
                      ? `Max: ${remainingCounts.dv}` 
                      : ''
                }
                sx={{
                  mt: 0.5,
                  "& .MuiOutlinedInput-root": { borderRadius: 3 },
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                    appearance: "textfield",
                  },
                  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                }}
              />
                </Grid>
                <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                name="fv_count"
                type="number"
                label="Field Visit"
                placeholder="Field Visit"
                value={formik.values.fv_count}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.fv_count && Boolean(formik.errors.fv_count)}
                helperText={
                  formik.touched.fv_count && formik.errors.fv_count 
                    ? formik.errors.fv_count 
                    : selectedPostTarget 
                      ? `Max: ${remainingCounts.fv}` 
                      : ''
                }
                sx={{
                  mt: 0.5,
                  "& .MuiOutlinedInput-root": { borderRadius: 3 },
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                    appearance: "textfield",
                  },
                  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                }}
              />
                </Grid>
                <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                name="fd_count"
                type="number"
                label="Field Day"
                placeholder="Field Day"
                value={formik.values.fd_count}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.fd_count && Boolean(formik.errors.fd_count)}
                helperText={
                  formik.touched.fd_count && formik.errors.fd_count 
                    ? formik.errors.fd_count 
                    : selectedPostTarget 
                      ? `Max: ${remainingCounts.fd}` 
                      : ''
                }
                sx={{
                  mt: 0.5,
                  "& .MuiOutlinedInput-root": { borderRadius: 3 },
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                    appearance: "textfield",
                  },
                  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                }}
              />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                size="small"
                onClick={addEntry}
                type="button"
              >
                Add
              </Button>
            </Grid>
          </Grid>
        </form>

        {entries.length > 0 && (
          <>
            {/* Mobile view (xs) */}
            <Paper elevation={0} sx={{ display: { xs: 'block', sm: 'none' }, mt: 3, borderRadius: 3, border: "1px solid #E5E7EB" }}>
              {entries.map((e, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: 2,
                    borderBottom: idx < entries.length - 1 ? '1px solid #E5E7EB' : 'none',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Post Target ID</Typography>
                    <Typography variant="caption">{e.post_target_id || "N/A"}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Month</Typography>
                    <Typography variant="caption">{e.month_year}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Dealer Visit</Typography>
                    <Typography variant="caption">{e.dv_count}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Field Visit</Typography>
                    <Typography variant="caption">{e.fv_count}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Field Day</Typography>
                    <Typography variant="caption">{e.fd_count}</Typography>
                  </Box>
                </Box>
              ))}
            </Paper>

            {/* Desktop view (sm and up) */}
            <Paper elevation={0} sx={{ display: { xs: 'none', sm: 'block' }, mt: 3, borderRadius: 3, border: "1px solid #E5E7EB" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Post Target ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Month (YYYY-MM)</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Dealer Visit</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Field Visit</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Field Day</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((e, i) => (
                    <TableRow key={i}>
                      <TableCell>{e.post_target_id || "N/A"}</TableCell>
                      <TableCell>{e.month_year}</TableCell>
                      <TableCell>{e.dv_count}</TableCell>
                      <TableCell>{e.fv_count}</TableCell>
                      <TableCell>{e.fd_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          sx={{ borderRadius: 3, px: 4, py: 1.25, boxShadow: "0 6px 16px rgba(37,99,235,0.35)" }}
          onClick={handleSubmitAll}
          disabled={createLoading || entries.length === 0}
        >
          {createLoading ? "Submitting..." : "Submit Data"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PostMonthlyTarget.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  defaultPostTargetId: PropTypes.number,
};

export default PostMonthlyTarget;